import { apiRequest } from '../lib/httpClient'
import { isAdvicePrepPriceNickname } from '@/features/adviceprep/pricing'

export type BillingSubscriptionStatus = 'active' | 'trialing' | 'canceled' | 'incomplete' | 'past_due'

/** The name AdvicePrep prices resolve to, and how the page picks its row out. */
export const ADVICEPREP_PLATFORM_NAME = 'AdvicePrep'

export function isAdvicePrepSubscription(subscription: { price: { platformName: string }[] }): boolean {
  return subscription.price.some((item) => item.platformName === ADVICEPREP_PLATFORM_NAME)
}

export type BillingPlanPrice = {
  platformName: string
  /** What is charged now, after any coupon. Zero for a 100%-off trial. */
  amount: number
  /**
   * The list price before any coupon, so a screen can still name what the
   * customer pays once the discount runs out. Reading `amount` for that during
   * a free trial promises a first charge of $0.00.
   */
  fullAmount: number
}

export type BillingInvoiceStatus = 'upcoming' | 'draft' | 'open' | 'paid' | 'uncollectible' | 'void'

export type BillingInvoiceLineKind = 'flat' | 'usage'

export type BillingInvoiceLine = {
  description: string
  quantity: number | null
  unitAmount: number | null
  amount: number
  kind: BillingInvoiceLineKind
}

/** Amounts are in dollars, the same unit as `amount` on the overview response. */
export type BillingInvoice = {
  invoiceId: string | null
  status: BillingInvoiceStatus
  periodStart: string | null
  periodEnd: string | null
  chargeDate: string | null
  total: number
  subtotal: number
  tax: number
  usageAmount: number
  lines: BillingInvoiceLine[]
}

export type BillingSubscription = {
  subscriptionId: string
  status: BillingSubscriptionStatus
  nextBillingDate: string | null
  currentPeriodEnd: string | null
  trialEnd?: string | null
  discountEnd?: string | null
  /**
   * A promo is on the subscription. Only a promo-code trial carries one, which is
   * what makes it eligible to end early. `discountEnd` cannot say this: a
   * forever coupon has no end.
   */
  hasDiscount?: boolean
  isExpired?: boolean
  price: BillingPlanPrice[]
  upcomingInvoice?: BillingInvoice | null
  latestInvoice?: BillingInvoice | null
}

export type BillingOverview = {
  clientId: string
  amountDue: number
  hasActiveSubscription: boolean
  hasPaymentMethod: boolean
  cardType: string | null
  cardLast4: string | null
  billingScheme: string
  subscriptions: BillingSubscription[]
}

export type BillingAccess = {
  hasBillingAccess: boolean
  isSubscribedToBas: boolean
  clientId: string
}

type StripeSubscriptionItem = {
  quantity?: number
  plan?: {
    nickname?: string
    metadata?: Record<string, unknown>
  }
  price?: {
    nickname?: string
    unit_amount_decimal?: string | number
  }
}

// Unvalidated wire shape: every field is checked before it reaches BillingInvoiceLine.
type StripeInvoiceLineSummary = {
  description?: unknown
  quantity?: unknown
  unitAmount?: unknown
  amount?: unknown
  kind?: unknown
}

type StripeInvoiceSummary = {
  id?: string | null
  status?: string
  periodStart?: number
  periodEnd?: number
  chargeDate?: number
  total?: number
  tax?: number
  lines?: unknown
}

type StripeSubscription = {
  id?: string
  status?: string
  currency?: string
  current_period_end?: number
  current_period_start?: number
  canceled_at?: number
  cancel_at_period_end?: boolean
  trialEnd?: number
  trial_end?: number
  trailStart?: number
  discount?: {
    start?: number
    end?: number
    coupon?: {
      percent_off?: number
    }
  }
  items?: {
    data?: StripeSubscriptionItem[]
  }
  upcomingInvoice?: StripeInvoiceSummary | null
  latestInvoice?: StripeInvoiceSummary | null
}

type StripeSubscriptionResponse = {
  amount?: number
  cardType?: string | null
  cardLast4?: string | null
  billingScheme?: string
  subscriptions?: StripeSubscription[]
}

type BillingVisibility = {
  adviceprep: boolean
  br: boolean
  xero: boolean
  fyi: boolean
  karbon: boolean
}

const USER_TYPES = {
  Admin: 1,
  ClientAdmin: 2,
  ClientUser: 3,
}

const CLIENT_TYPES = {
  Standard: 1,
  Advisor: 3,
}

const PLATFORM_TYPE = {
  Xero: 1,
  BRWFM: 6,
  Karbon: 7,
  FYI: 8,
}

function toIsoDate(unixSeconds?: number): string | null {
  if (!unixSeconds) return null
  const millis = unixSeconds * 1000
  const date = new Date(millis)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

const INVOICE_STATUSES: readonly BillingInvoiceStatus[] = ['upcoming', 'draft', 'open', 'paid', 'uncollectible', 'void']
const INVOICE_LINE_KINDS: readonly BillingInvoiceLineKind[] = ['flat', 'usage']

function finiteOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function roundToCents(value: number): number {
  return Math.round(value * 100) / 100
}

function normalizeInvoice(
  invoice: StripeInvoiceSummary | null | undefined,
  statusOverride?: BillingInvoiceStatus
): BillingInvoice | null {
  if (!invoice) return null
  const total = finiteOrNull(invoice.total)
  const status = statusOverride ?? INVOICE_STATUSES.find((candidate) => candidate === invoice.status)
  if (total === null || !status) return null

  // All or nothing: a dropped line would resurface as tax in `total - subtotal`,
  // and an unknown kind would move usage into the flat fee.
  const rawLines: unknown[] = Array.isArray(invoice.lines) ? invoice.lines : []
  const lines = rawLines.flatMap((raw): BillingInvoiceLine[] => {
    if (!isRecord(raw)) return []
    const line: StripeInvoiceLineSummary = raw
    const amount = finiteOrNull(line.amount)
    const kind = INVOICE_LINE_KINDS.find((candidate) => candidate === line.kind)
    const description = line.description ?? ''
    if (amount === null || !kind || typeof description !== 'string') return []
    return [
      {
        description,
        quantity: finiteOrNull(line.quantity),
        unitAmount: finiteOrNull(line.unitAmount),
        amount,
        kind,
      },
    ]
  })
  if (lines.length === 0 || lines.length !== rawLines.length) return null

  const subtotal = roundToCents(lines.reduce((sum, line) => sum + line.amount, 0))

  return {
    invoiceId: invoice.id || null,
    status,
    periodStart: toIsoDate(invoice.periodStart),
    periodEnd: toIsoDate(invoice.periodEnd),
    chargeDate: toIsoDate(invoice.chargeDate),
    total,
    subtotal,
    // Line amounts exclude GST but `total` includes it, so without an explicit
    // figure the gap between them is the tax.
    tax: finiteOrNull(invoice.tax) ?? roundToCents(total - subtotal),
    usageAmount: roundToCents(
      lines.filter((line) => line.kind === 'usage').reduce((sum, line) => sum + line.amount, 0)
    ),
    lines,
  }
}

function getVisibility(session: {
  currentUserTypeId?: number
  currentClientTypeId?: number
  advisorId?: string | null
  platformType?: number[]
}): BillingVisibility {
  const isAdmin = session.currentUserTypeId === USER_TYPES.Admin
  const isAdvisorClientAdmin =
    session.currentUserTypeId === USER_TYPES.ClientAdmin &&
    session.currentClientTypeId === CLIENT_TYPES.Advisor &&
    !session.advisorId

  if (isAdmin || isAdvisorClientAdmin) {
    return { br: true, xero: true, fyi: true, karbon: true, adviceprep: true }
  }

  const isStandardClientAdmin =
    (session.currentUserTypeId === USER_TYPES.ClientAdmin ||
      session.currentUserTypeId === USER_TYPES.ClientUser) &&
    session.currentClientTypeId === CLIENT_TYPES.Standard

  if (!isStandardClientAdmin) {
    return { br: false, xero: false, fyi: false, karbon: false, adviceprep: true }
  }

  const platforms = session.platformType || []
  return {
    br: platforms.includes(PLATFORM_TYPE.BRWFM),
    xero: platforms.includes(PLATFORM_TYPE.Xero),
    fyi: platforms.includes(PLATFORM_TYPE.FYI),
    karbon: platforms.includes(PLATFORM_TYPE.Karbon),
    // AdvicePrep has no PlatformType, so it cannot be gated on the session's
    // platform list the way the four products are.
    adviceprep: true,
  }
}

/**
 * Stripe reports a pending cancellation as `active` with `cancel_at_period_end`
 * set, and only says `canceled` once the period has ended. The page renders a
 * pending cancellation as `canceled` with `isExpired` false, so the flag has to
 * be read here rather than inferred from the status string alone (spec 5.3).
 */
function mapStatus(
  status?: string,
  cancelAtPeriodEnd?: boolean
): BillingSubscriptionStatus | null {
  if (status === 'active') return cancelAtPeriodEnd ? 'canceled' : 'active'
  if (status === 'trialing' || status === 'canceled' || status === 'incomplete') return status
  if (status === 'past_due') return 'past_due'
  return null
}

function getPlatformNameAndAllowed(
  nickname: string,
  visibility: BillingVisibility,
  options?: { forceAllow?: boolean }
) {
  const forceAllow = options?.forceAllow ?? false

  if (nickname.startsWith('BR') && (visibility.br || forceAllow)) {
    return { platformName: 'Bluerock WorkflowMax', allowed: true, isXero: false }
  }
  if (nickname.startsWith('Xero') && (visibility.xero || forceAllow)) {
    return { platformName: 'Xero', allowed: true, isXero: true }
  }
  if (nickname.startsWith('FYI') && (visibility.fyi || forceAllow)) {
    return { platformName: 'FYI', allowed: true, isXero: false }
  }
  if (nickname.startsWith('Karbon') && (visibility.karbon || forceAllow)) {
    return { platformName: 'Karbon', allowed: true, isXero: false }
  }
  if (isAdvicePrepPriceNickname(nickname) && (visibility.adviceprep || forceAllow)) {
    return { platformName: ADVICEPREP_PLATFORM_NAME, allowed: true, isXero: false }
  }
  return { platformName: '', allowed: false, isXero: false }
}

function normalizeOverview(
  clientId: string,
  payload: StripeSubscriptionResponse | null | undefined,
  visibility: BillingVisibility
): BillingOverview {
  if (!payload) {
    return {
      clientId,
      amountDue: 0,
      hasActiveSubscription: false,
      hasPaymentMethod: false,
      cardType: null,
      cardLast4: null,
      billingScheme: '',
      subscriptions: [],
    }
  }

  const subscriptions: BillingSubscription[] = []
  let amountDue = 0
  let hasActiveSubscription = false
  const now = Date.now()

  for (const subscription of payload.subscriptions || []) {
    const status = mapStatus(subscription.status, subscription.cancel_at_period_end)
    if (!status) continue
    const allowTrialVisibility = status === 'trialing'

    // A missing period end is unknown, not elapsed. Defaulting it to 0 made
    // `0 <= now` true and rendered a running subscription as already ended.
    const isExpired =
      status === 'canceled' && subscription.current_period_end
        ? subscription.current_period_end * 1000 <= now
        : false
    // A pending cancellation still bills the current period, so it stays in the
    // total. Dropping it would show an amount that disagrees with what Stripe
    // charges — for every product, not just AdvicePrep.
    const billsThisPeriod = status === 'active' || (status === 'canceled' && !isExpired)

    let hasXeroPrice = false
    let itemsDue = 0
    const priceByPlatform = new Map<string, number>()
    const fullPriceByPlatform = new Map<string, number>()

    for (const item of subscription.items?.data || []) {
      const nickname = item.plan?.nickname || item.price?.nickname || ''
      if (!nickname) continue

      const mapped = getPlatformNameAndAllowed(nickname, visibility, { forceAllow: allowTrialVisibility })
      if (!mapped.allowed) continue

      const unitAmount = Number(item.price?.unit_amount_decimal ?? 0) / 100
      const quantity = Number(item.quantity ?? 0)
      if (!Number.isFinite(unitAmount) || !Number.isFinite(quantity)) continue

      const percentOff = Number(subscription.discount?.coupon?.percent_off ?? 0)
      const discountMultiplier = 1 - percentOff / 100
      let fullAmount = unitAmount * quantity
      let currentAmount = fullAmount * discountMultiplier

      if (mapped.isXero && !hasXeroPrice) {
        hasXeroPrice = true
        currentAmount = Number(payload.amount ?? 0)
        // The server figure is already the real charge, with no coupon to undo.
        fullAmount = currentAmount
      }

      itemsDue += currentAmount

      priceByPlatform.set(
        mapped.platformName,
        (priceByPlatform.get(mapped.platformName) || 0) + currentAmount
      )
      fullPriceByPlatform.set(
        mapped.platformName,
        (fullPriceByPlatform.get(mapped.platformName) || 0) + fullAmount
      )
    }

    if (priceByPlatform.size === 0) {
      continue
    }

    // Metered usage has no unit price or quantity on the subscription items, so
    // only the upcoming invoice knows the real charge.
    const upcomingInvoice = normalizeInvoice(subscription.upcomingInvoice, 'upcoming')
    if (billsThisPeriod) {
      amountDue += upcomingInvoice?.total ?? itemsDue
    }

    hasActiveSubscription =
      hasActiveSubscription || status === 'active' || status === 'trialing' || status === 'incomplete'

    subscriptions.push({
      subscriptionId: subscription.id || '',
      status,
      nextBillingDate: toIsoDate(subscription.current_period_end),
      currentPeriodEnd: toIsoDate(subscription.current_period_end),
      trialEnd: toIsoDate(subscription.trialEnd ?? subscription.trial_end),
      discountEnd: toIsoDate(subscription.discount?.end),
      hasDiscount: !!subscription.discount,
      isExpired,
      price: Array.from(priceByPlatform.entries()).map(([platformName, amount]) => ({
        platformName,
        amount,
        fullAmount: fullPriceByPlatform.get(platformName) ?? amount,
      })),
      upcomingInvoice,
      latestInvoice: normalizeInvoice(subscription.latestInvoice),
    })
  }

  return {
    clientId,
    amountDue,
    hasActiveSubscription,
    hasPaymentMethod: !!(payload.cardType && payload.cardLast4),
    cardType: payload.cardType || null,
    cardLast4: payload.cardLast4 || null,
    billingScheme: payload.billingScheme || '',
    subscriptions,
  }
}

export async function fetchBillingOverview(
  clientId: string,
  session: {
    currentUserTypeId?: number
    currentClientTypeId?: number
    advisorId?: string | null
    platformType?: number[]
  }
): Promise<BillingOverview> {
  const payload = await apiRequest<StripeSubscriptionResponse | null>('user/ui/stripe/subscription', {
    method: 'GET',
    query: { clientId },
  })

  return normalizeOverview(clientId, payload, getVisibility(session))
}

export async function fetchBillingAccess(clientId: string): Promise<BillingAccess> {
  const payload = await apiRequest<Partial<BillingAccess>>('client/ui/billing/access', {
    method: 'GET',
    query: { clientId },
  })

  return {
    hasBillingAccess: Boolean(payload.hasBillingAccess),
    isSubscribedToBas: Boolean(payload.isSubscribedToBas),
    clientId: payload.clientId || clientId,
  }
}

export async function cancelSubscription(input: { clientId: string; subscriptionId: string }) {
  return apiRequest<{ status?: string }>('user/ui/stripe/subscription/cancel', {
    method: 'POST',
    body: {
      subscriptionId: input.subscriptionId,
      clientId: input.clientId,
    },
  })
}

export async function updatePaymentMethod(input: { clientId: string; paymentId: string }) {
  return apiRequest<{ error?: string }>('user/ui/stripe/payment/update', {
    method: 'POST',
    body: {
      paymentId: input.paymentId,
      clientId: input.clientId,
    },
  })
}

